import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field } from "@/components/panel";
import { checks } from "@/lib/validation";

const ROLES = [{ v: "admin", l: "Admin" }, { v: "super_admin", l: "Super admin" }];
const ESTADO = { activo: "Activo", invitado: "Invitación pendiente", desactivado: "Desactivado" };
const SEL = "rounded-xl border-2 border-[var(--brand-border)] px-3 py-1.5 text-sm outline-none focus:border-[var(--brand-accent)] bg-white";

const InviteLink = ({ link, email, testid = "invite-link-fallback" }) => (
  <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 text-xs" data-testid={testid}>
    <p className="font-bold mb-1">No se pudo enviar el mail{email ? ` a ${email}` : ""}. Compartí este link de un solo uso (vence en 72 h):</p>
    <div className="flex gap-2 items-center">
      <code className="break-all flex-1">{link}</code>
      <button className="btn-outline text-xs" data-testid="invite-link-copy"
        onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Link copiado"))}><Copy size={14} /></button>
    </div>
  </div>
);

const NuevoUsuario = ({ onDone }) => {
  const [f, setF] = useState({ email: "", name: "", rol: "admin" });
  const [link, setLink] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const msg = checks.email(f.email) || checks.required(f.name, "Ingresá el nombre");
    if (msg) return toast.error(msg);
    try {
      const r = await api.post("/superadmin/usuarios", f);
      setLink(r.data.invite_link);
      toast.success(r.data.email_enviado ? "Invitación enviada por mail" : "Usuario creado. El mail no salió: copiá el link de invitación");
      setF({ email: "", name: "", rol: "admin" });
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo crear el usuario"); }
  };
  return (
    <form onSubmit={submit} noValidate className="card-brand p-5 space-y-3" data-testid="internal-user-form">
      <h3 className="font-heading font-bold text-[var(--brand-primary)]">Invitar usuario interno</h3>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="internal-user-email" />
        <Field label="Nombre" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="internal-user-name" />
        <label className="block"><span className="text-xs font-semibold text-[var(--brand-primary)]">Rol</span>
          <select className={SEL + " mt-1 w-full py-2.5"} value={f.rol} onChange={(e) => setF({ ...f, rol: e.target.value })} data-testid="internal-user-rol">{ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}</select>
        </label>
      </div>
      <p className="text-xs text-[var(--brand-text-muted)]">No se define contraseña: la persona recibe un link de invitación y crea la suya.</p>
      <button className="btn-primary flex items-center gap-2" data-testid="internal-user-submit"><UserPlus size={16} /> Invitar</button>
      {link && <InviteLink link={link} testid="invite-link-fallback-new" />}
    </form>
  );
};

const Fila = ({ u, me, onChange, onLink }) => {
  const act = async (fn, ok) => {
    try {
      const r = await fn();
      if (r?.data?.invite_link) { toast.warning("El mail no salió: copiá el link de invitación"); onLink({ link: r.data.invite_link, email: u.email }); }
      else toast.success(ok);
      onChange();
    }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const self = u.id === me;
  return (
    <tr className="border-t border-slate-100" data-testid={`internal-user-row-${u.id}`}>
      <td className="py-2 pr-3">{u.name}<div className="text-xs text-[var(--brand-text-muted)]">{u.email}</div></td>
      <td className="py-2 pr-3">
        <select className={SEL + " text-xs"} value={u.rol} disabled={self} data-testid={`internal-user-rol-${u.id}`}
          onChange={(e) => act(() => api.put(`/superadmin/usuarios/${u.id}/rol`, { rol: e.target.value }), "Rol actualizado")}>
          {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
        </select>
      </td>
      <td className="py-2 pr-3 text-xs" data-testid={`internal-user-estado-${u.id}`}>{ESTADO[u.estado] || u.estado}</td>
      <td className="py-2 pr-3 text-xs" data-testid={`internal-user-mfa-${u.id}`}>{u.rol === "super_admin" ? (u.mfa_activo ? "Activo" : "Pendiente") : "—"}</td>
      <td className="py-2 flex gap-2 flex-wrap">
        {!self && u.estado !== "desactivado" && <button className="btn-outline text-xs" data-testid={`internal-user-disable-${u.id}`}
          onClick={() => act(() => api.put(`/superadmin/usuarios/${u.id}/estado`, { activo: false }), "Usuario desactivado")}>Desactivar</button>}
        {!self && u.estado === "desactivado" && <button className="btn-outline text-xs" data-testid={`internal-user-enable-${u.id}`}
          onClick={() => act(() => api.put(`/superadmin/usuarios/${u.id}/estado`, { activo: true }), "Usuario reactivado")}>Reactivar</button>}
        {!self && u.estado !== "desactivado" && <button className="btn-outline text-xs" data-testid={`internal-user-reinvite-${u.id}`}
          onClick={() => act(() => api.post(`/superadmin/usuarios/${u.id}/invitacion`), "Invitación generada")}>Reenviar invitación</button>}
        {!self && u.mfa_activo && <button className="btn-outline text-xs" data-testid={`internal-user-mfa-reset-${u.id}`}
          onClick={() => window.confirm(`¿Resetear la verificación en dos pasos de ${u.email}? Se cerrarán sus sesiones y deberá volver a enrolarse.`)
            && act(() => api.post(`/superadmin/usuarios/${u.id}/mfa/reset`), "MFA reseteado")}>Resetear MFA</button>}
      </td>
    </tr>
  );
};

export const UsuariosInternos = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [link, setLink] = useState(null);
  const load = () => api.get("/superadmin/usuarios").then((r) => setItems(r.data.items)).catch(() => toast.error("No se pudo cargar la lista"));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6" data-testid="internal-users-panel">
      <NuevoUsuario onDone={load} />
      {link && <InviteLink link={link.link} email={link.email} />}
      <div className="card-brand p-5 overflow-x-auto">
        <table className="w-full text-sm" data-testid="internal-users-table">
          <thead><tr className="text-left text-xs text-[var(--brand-text-muted)]"><th className="pb-2">Usuario</th><th className="pb-2">Rol</th><th className="pb-2">Estado</th><th className="pb-2">MFA</th><th className="pb-2">Acciones</th></tr></thead>
          <tbody>{items.map((u) => <Fila key={u.id} u={u} me={user?.id} onChange={load} onLink={setLink} />)}</tbody>
        </table>
      </div>
    </div>
  );
};
